import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { useCommitmentPaymentPlanUpdate } from '@sola/my-sdk/commitments/hooks.js';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.js';
import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { userErrorAssignPaymentMethodToCommitment } from '@sola/shared-utils/errors/supporters.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.js';
import { paymentMethodsQuery } from '@sola/supporter-sdk/queries.js';

import {
  PaymentMethodIcon,
  getPaymentMethodLabel,
} from '../../atoms/payment-method-icon/payment-method-icon';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts';
import {
  createPaymentPlanSuccessAlert,
  createSupportAlert,
} from '../../utils/alerts';
import { getErrorTranslationKeys } from '../../utils/errors';
import { PaymentMethodChoiceDialog } from '../payment-method-choice-dialog/payment-method-choice-dialog';

export const CommitmentDetailsPaymentPlan: React.FC<{
  commitment?: GraphQLCommitment | null;
}> = ({ commitment }) => {
  const { t } = useTranslation('my');
  const [isDialogOpen, setisDialogOpen] = useState(false);
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const {
    alerts: sheetAlerts,
    setAlerts: setSheetAlerts,
    clearAlerts: clearSheetAlerts,
    handleOpenChange,
  } = useModalAlerts();
  const { data } = useSupporterQuery(paymentMethodsQuery);
  const paymentPlanUpdate = useCommitmentPaymentPlanUpdate(
    commitment?.internalId,
  );
  const paymentMethodForCommitment = useMemo(() => {
    if (!data?.length || !commitment?.paymentMethodId) {
      return undefined;
    }

    return data.find((pm) => pm?.id === commitment?.paymentMethodId);
  }, [data, commitment?.paymentMethodId]);

  const handlePaymentMethodSelection = async (
    paymentMethodId: GraphQLSupporterPaymentMethod['id'],
    paymentDay?: number,
  ) => {
    clearSheetAlerts();
    clearAlerts();

    try {
      const { success } = await paymentPlanUpdate.mutateAsync({
        newPaymentMethodId: paymentMethodId,
        paymentDay,
      });

      if (!success) {
        throw userErrorAssignPaymentMethodToCommitment();
      }

      addAlerts(createPaymentPlanSuccessAlert());

      // TODO: Add "Almost Done" alert

      handleOpenChange(false, setisDialogOpen);
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(
        SupporterErrorTypeEnum.AssignPaymentMethodToCommitment,
        errors,
      );
      setSheetAlerts(
        translationKeys.map((translationKey) => {
          return createSupportAlert(translationKey);
        }),
      );
    }
  };

  if (paymentMethodForCommitment) {
    return (
      <>
        <div className="flex gap-comfy-md items-center">
          <PaymentMethodIcon
            paymentMethod={
              paymentMethodForCommitment as GraphQLSupporterPaymentMethod
            }
          />
          <span className="label-moderate label-lg">
            {getPaymentMethodLabel(paymentMethodForCommitment, t)}
          </span>
        </div>
        <LinkStyle noUnderline inline>
          <button
            type="button"
            onClick={() => {
              setisDialogOpen((prev) => !prev);
            }}
          >
            {t('commitments.details.paymentMethod.changeLabel')}
          </button>
        </LinkStyle>
        <PaymentMethodChoiceDialog
          currentPaymentMethod={paymentMethodForCommitment}
          currentPaymentDay={commitment?.paymentPullDay}
          handlePaymentMethodSelection={handlePaymentMethodSelection}
          open={isDialogOpen}
          onOpenChange={setisDialogOpen}
          isBusy={paymentPlanUpdate.isPending}
          // TODO: Prop-stuffing shouldn't be needed, but I'm missing something with the alerts provider
          dialogAlerts={sheetAlerts}
          // TODO: Should be ok to allow adding here, yeah?
          allowAddPaymentMethod
        />
      </>
    );
  }

  if (!paymentMethodForCommitment) {
    return (
      <>
        <Alert
          variant="warning"
          title={t('commitments.notRecurring.title')}
          message={t('commitments.notRecurring.message')}
          action={
            <button
              type="button"
              onClick={() => {
                setisDialogOpen((prev) => !prev);
              }}
            >
              {t('my:commitments.notRecurring.ctaText')}
            </button>
          }
        />
        <PaymentMethodChoiceDialog
          handlePaymentMethodSelection={handlePaymentMethodSelection}
          open={isDialogOpen}
          onOpenChange={setisDialogOpen}
          isBusy={paymentPlanUpdate.isPending}
          // TODO: Prop-stuffing shouldn't be needed, but I'm missing something with the alerts provider
          dialogAlerts={sheetAlerts}
          allowAddPaymentMethod
        />
      </>
    );
  }
};
